import { type CSSProperties } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export const ADAM_CURSOR_TRAVEL_MS = 1050;

type JsmPlaygroundAdamCursorProps = {
	hasUserTakenOver: boolean;
	isPressed: boolean;
	isTransitionEnabled: boolean;
	isVisible: boolean;
	x: number;
	y: number;
};

export const JsmPlaygroundAdamCursor = ({
	hasUserTakenOver,
	isPressed,
	isTransitionEnabled,
	isVisible,
	x,
	y,
}: JsmPlaygroundAdamCursorProps) => {
	const isShown = isVisible && !hasUserTakenOver;

	return (
		<div
			aria-hidden="true"
			css={cursorStyles}
			data-testid="jsm-playground-adam-cursor"
			style={
				{
					'--adam-cursor-opacity': isShown ? 1 : 0,
					'--adam-cursor-scale': isShown ? 1 : 0.92,
					'--adam-cursor-transition': isTransitionEnabled
						? `transform ${ADAM_CURSOR_TRAVEL_MS}ms cubic-bezier(0.33, 0, 0.2, 1)`
						: 'transform 0ms',
					'--adam-cursor-x': `${x}px`,
					'--adam-cursor-y': `${y}px`,
				} as CSSProperties
			}
		>
			<svg
				css={[cursorArrowStyles, isPressed && cursorArrowPressedStyles]}
				fill="none"
				height="24"
				viewBox="0 0 23 23"
				width="24"
			>
				<path
					d="M7.735 22.62 0 0l22.585 7.771-9.899 4.951-4.951 9.898Z"
					fill="currentColor"
					stroke="white"
					strokeWidth="1.5"
				/>
			</svg>
			<span css={cursorLabelStyles}>Adam</span>
		</div>
	);
};

const cursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 700,
	pointerEvents: 'none',
	opacity: 'var(--adam-cursor-opacity)',
	transform:
		'translate3d(var(--adam-cursor-x), var(--adam-cursor-y), 0) scale(var(--adam-cursor-scale))',
	transformOrigin: 'top left',
	transition: 'var(--adam-cursor-transition), opacity 400ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

const cursorArrowStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	color: token('color.background.accent.blue.bolder'),
	filter: `drop-shadow(0 2px 6px ${token('color.blanket')})`,
	transform: 'scale(1)',
	transformOrigin: 'top left',
	transition: 'transform 200ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

const cursorArrowPressedStyles = css({ transform: 'scale(0.9)' });

const cursorLabelStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.150'),
	insetInlineStart: token('space.300'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: token('space.025'),
	paddingInline: token('space.150'),
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.blue.bolder'),
	color: token('color.text.inverse'),
	font: token('font.heading.medium'),
	whiteSpace: 'nowrap',
	boxShadow: token('elevation.shadow.overlay'),
});
